/**
 * Jednotné formátování čísel, hodin, částek a dat pro celé UI (česky, cs-CZ).
 *
 * Pravidla: desetinná čárka, mezera mezi tisíci, datum „6. 10. 2026", hodiny „7,5 h".
 * Na server jdou pořád čistá čísla s tečkou — formátuje se jen pro zobrazení.
 */

const LOCALE = 'cs-CZ'

type NumLike = number | string | null | undefined

function toNumber(value: NumLike): number {
  if (value === null || value === undefined || value === '') return NaN
  return typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
}

/** Číslo česky („1 250,5"). `maxDecimals` = nejvýš kolik desetin, nuly na konci se nepíšou. */
export function formatNumber(value: NumLike, maxDecimals = 2, minDecimals = 0): string {
  const n = toNumber(value)
  if (!Number.isFinite(n)) return '–'
  return n.toLocaleString(LOCALE, { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals })
}

/** Hodiny („7,5 h", „0 h"). Prázdná hodnota = „0 h". */
export function formatHours(value: NumLike, withUnit = true): string {
  const n = toNumber(value)
  const text = formatNumber(Number.isFinite(n) ? n : 0, 2)
  return withUnit ? `${text} h` : text
}

/** Částka v Kč („12 500 Kč", „12,50 Kč"). `decimals` = pevný počet desetin (výchozí bez haléřů, pokud nejsou). */
export function formatMoney(value: NumLike, decimals?: number): string {
  let n = toNumber(value)
  if (!Number.isFinite(n)) return '–'
  // Záporná nula (zaokrouhlení „-0,00") se ukáže jako 0.
  const shown = decimals === undefined ? (Number.isInteger(n) ? 0 : 2) : decimals
  if (Math.abs(n) < 0.5 * 10 ** -shown) n = 0
  const opts = decimals === undefined
    ? { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }
    : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  return `${n.toLocaleString(LOCALE, opts)} Kč`
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null
  // „2026-10-06" bez času parsovat jako MÍSTNÍ datum, jinak by v UTC−x vyšel předchozí den.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Datum „6. 10. 2026". */
export function formatDate(value: Date | string | null | undefined): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(LOCALE) : '–'
}

/** Krátké datum bez roku „6. 10.". */
export function formatDateShort(value: Date | string | null | undefined): string {
  const d = toDate(value)
  return d ? `${d.getDate()}. ${d.getMonth() + 1}.` : '–'
}

/** Datum a čas „6. 10. 2026 14:05". */
export function formatDateTime(value: Date | string | null | undefined): string {
  const d = toDate(value)
  if (!d) return '–'
  return `${d.toLocaleDateString(LOCALE)} ${d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' })}`
}

/** Dnešní datum ve tvaru „YYYY-MM-DD" (místní čas, ne UTC). */
export function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * České skloňování podle počtu: plural(3, 'zakázka', 'zakázky', 'zakázek') → „zakázky".
 * 1 → první tvar, 2–4 → druhý, 0 a 5+ → třetí.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const n = Math.abs(count)
  if (n === 1) return one
  if (n >= 2 && n <= 4) return few
  return many
}
